<template>
  <ThePage :title="i18n(translations.title)">
    <template #pageTitle>
      {{ i18n(translations.title) }}
    </template>

    <template #pageContent>
      <Filters
        v-model:filtersValues="filtersValues"
        :resetFilters
        :submitFilters
      />

      <RevCard class="mt-32">
        <RevLoadingScreen
          v-if="isLoading"
          class="mt-24 p-24"
          :text="i18n(translations.loading)"
        />
        <NoResults v-if="shouldShowNoResults" class="p-16" />

        <div v-if="shouldShowResults" class="mb-24 flex p-16">
          <span class="heading-3 mr-8">
            {{ i18n(translations.reviewsCount, { count: data!.count }) }}
          </span>
          <AverageRating class="flex" :rating="data!.rate" />
        </div>
        <ReviewsTable v-if="shouldShowResults" :data="data!" />
      </RevCard>
    </template>
  </ThePage>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { useFilters } from '~/composables/useFilters'

import translations from './CustomerReviews.translations'
import AverageRating from './components/AverageRating/AverageRating.vue'
import { getInitialFiltersValues } from './components/Filters/Filters.utils'
import Filters from './components/Filters/Filters.vue'
import NoResults from './components/NoResults/NoResults.vue'
import ReviewsTable from './components/ReviewsTable/ReviewsTable.vue'

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const initialFiltersValues = getInitialFiltersValues()

const { sanitizedFiltersValues, filtersValues, submitFilters, resetFilters } =
  useFilters(initialFiltersValues)

const {
  data,
  error,
  pending: isLoading,
} = await useHttpFetch(merchantsAPI.getReviews, {
  queryParams: sanitizedFiltersValues,
  watch: [sanitizedFiltersValues],
})

const shouldShowResults = computed(
  () => !!data?.value?.results?.length && !isLoading.value,
)

const shouldShowNoResults = computed(
  () => !isLoading.value && (error.value || !data?.value?.results?.length),
)

watch(
  error,
  () => {
    if (error.value) {
      openErrorToast()
    }
  },
  { immediate: true },
)
</script>
